import useU2000LocalStorage from "../U2000UseLocalStorage";
import { useU2000AuthContext } from "../../context/auth/U2000UseAuthContext";
import { jwtDecode } from "jwt-decode";

interface OrganizationPersistenceData {
  uuid: string;
  organizationName: string;
  persistedAt: number;
}

interface OrganizationPersistenceHook {
  persistedOrganization: OrganizationPersistenceData | null;
  persistOrganization: (uuid: string, organizationName: string) => void;
  clearPersistedOrganization: () => void;
  isOrganizationPersisted: (uuid: string) => boolean;
}

const useU2000OrganizationPersistence = (): OrganizationPersistenceHook => {
  const { auth } = useU2000AuthContext();

  // Create user-specific key for organization persistence using token.sub
  const getUserSpecificKey = () => {
    // Handle case when token is not available (login page)
    if (!auth.token) return "U2001.lastOrganization.guest";

    try {
      const userContext: any = jwtDecode(auth.token);
      return `U2001.lastOrganization.${userContext.sub}`;
    } catch (error) {
      // Fallback if token decode fails
      return "U2001.lastOrganization.guest";
    }
  };

  const [persistedOrganization, setPersistedOrganization] = useU2000LocalStorage<OrganizationPersistenceData | null>(getUserSpecificKey(), null);

  const persistOrganization = (uuid: string, organizationName: string) => {
    const organizationData: OrganizationPersistenceData = {
      uuid,
      organizationName,
      persistedAt: Date.now(),
    };
    setPersistedOrganization(organizationData);
  };

  const clearPersistedOrganization = () => {
    setPersistedOrganization(null);
  };

  const isOrganizationPersisted = (uuid: string): boolean => {
    return persistedOrganization?.uuid === uuid;
  };

  return {
    persistedOrganization,
    persistOrganization,
    clearPersistedOrganization,
    isOrganizationPersisted,
  };
};

export default useU2000OrganizationPersistence;
export type { OrganizationPersistenceData, OrganizationPersistenceHook };
